import { useState, useEffect } from 'react'
import { CreatePetDto, Pet } from '../../types/Pet'
import { petService } from '../../services/petService'

interface AddEditPetModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (pet: CreatePetDto) => Promise<void>
  onDelete?: () => Promise<void>
  pet?: Pet | CreatePetDto | null
}

export default function AddEditPetModal({ isOpen, onClose, onSave, onDelete, pet }: AddEditPetModalProps) {
  const isEditing = pet && 'id' in pet
  const [formData, setFormData] = useState<CreatePetDto>(getInitialData(pet))

  useEffect(() => {
    setFormData(getInitialData(pet))
  }, [pet])

  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSaving(true)

    try {
      // Clean up the data - convert empty strings to undefined for optional fields
      const cleanedData: CreatePetDto = {
        name: formData.name,
        species: formData.species,
        breed: formData.breed || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        gender: formData.gender || undefined,
        weight: formData.weight,
        microchipId: formData.microchipId || undefined,
        emergencyContact: formData.emergencyContact || undefined,
        emergencyPhone: formData.emergencyPhone || undefined,
        medicalNotes: formData.medicalNotes || undefined,
        allergies: formData.allergies || undefined,
        medications: formData.medications || undefined,
      }

      await onSave(cleanedData)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pet')
    } finally {
      setIsSaving(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: name === 'weight' ? (value ? parseFloat(value) : undefined) : value
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {isEditing ? 'Edit Pet' : 'Add Pet'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSaving}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Photo Upload */}
          {isEditing && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Photo</h3>
              {(pet as Pet)?.photoUrl && (
                <img src={(pet as Pet).photoUrl!} alt="Pet" className="w-24 h-24 rounded-full object-cover mb-3" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file || !isEditing) return
                  try {
                    await petService.uploadPhoto((pet as Pet).id, file)
                  } catch (err) {
                    console.error('Upload failed:', err)
                  }
                }}
                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-600 file:font-medium hover:file:bg-primary-100"
              />
              <p className="text-xs text-gray-400 mt-1">Max 5MB. JPG, PNG, or HEIC.</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pet Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-field w-full"
                placeholder="Enter pet name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Species <span className="text-red-500">*</span>
              </label>
              <select
                name="species"
                value={formData.species}
                onChange={handleChange}
                required
                className="input-field w-full"
              >
                <option value="Dog">Dog</option>
                <option value="Cat">Cat</option>
                <option value="Bird">Bird</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Breed
              </label>
              <input
                type="text"
                name="breed"
                value={formData.breed}
                onChange={handleChange}
                className="input-field w-full"
                placeholder="e.g., Golden Retriever"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="input-field w-full"
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight (lbs)
                </label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight || ''}
                  onChange={handleChange}
                  step="0.1"
                  className="input-field w-full"
                  placeholder="e.g., 45.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Microchip ID
                </label>
                <input
                  type="text"
                  name="microchipId"
                  value={formData.microchipId}
                  onChange={handleChange}
                  className="input-field w-full"
                  placeholder="Microchip number"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Emergency Contact</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name
              </label>
              <input
                type="text"
                name="emergencyContact"
                value={formData.emergencyContact}
                onChange={handleChange}
                className="input-field w-full"
                placeholder="Emergency contact name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                name="emergencyPhone"
                value={formData.emergencyPhone}
                onChange={handleChange}
                className="input-field w-full"
                placeholder="Phone number"
              />
            </div>
          </div>

          {/* Medical Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Medical Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergies
              </label>
              <input
                type="text"
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                className="input-field w-full"
                placeholder="Any allergies"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medications
              </label>
              <input
                type="text"
                name="medications"
                value={formData.medications}
                onChange={handleChange}
                className="input-field w-full"
                placeholder="Current medications"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Notes
              </label>
              <textarea
                name="medicalNotes"
                value={formData.medicalNotes}
                onChange={handleChange}
                rows={4}
                className="input-field w-full"
                placeholder="Any additional medical information"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !formData.name}
              className="btn-primary flex-1"
            >
              {isSaving ? 'Saving...' : isEditing ? 'Update Pet' : 'Add Pet'}
            </button>
          </div>

          {isEditing && onDelete && (
            <div className="pt-4 border-t mt-4">
              <button
                type="button"
                onClick={() => {
                  if (confirm('Are you sure you want to delete this pet? This cannot be undone.')) {
                    onDelete()
                  }
                }}
                className="text-red-500 hover:text-red-700 text-sm w-full text-center"
              >
                Delete this pet
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

function getInitialData(pet?: Pet | CreatePetDto | null): CreatePetDto {
  if (!pet) return { name: '', species: 'Dog' }
  const p = pet as unknown as Record<string, unknown>
  return {
    name: (p.name as string) || '',
    species: (p.species as string) || 'Dog',
    breed: (p.breed as string) || '',
    dateOfBirth: (p.dateOfBirth as string) || '',
    gender: (p.gender as string) || '',
    weight: p.weight as number | undefined,
    microchipId: (p.microchipId as string) || '',
    emergencyContact: (p.emergencyContact as string) || '',
    emergencyPhone: (p.emergencyPhone as string) || '',
    medicalNotes: (p.medicalNotes as string) || '',
    allergies: (p.allergies as string) || '',
    medications: (p.medications as string) || '',
  }
}
